<template>
  <div class="space-y-24">
    <div>
      <p class="text-static-default-hi body-1">
        {{ i18n(translations.categoryLabel) }}
        <span aria-hidden="true">*</span>
      </p>

      <RevRadio
        v-for="option in categoryOptions"
        :id="`report-illicit-content-category-${option.value}`"
        :key="option.value"
        :model-value="values.category === option.value"
        :value="option.value"
        variant="full"
        class="space-y-8"
        @update:model-value="values.category = option.value"
      >
        <template #label>
          {{ option.short }}
        </template>
        <template #description>
          {{ option.full }}
        </template>
      </RevRadio>

      <p v-if="errors.category" class="text-action-danger-hi body-2 mt-4">
        {{ errors.category }}
      </p>
    </div>

    <div
      v-if="CONDITIONAL_CATEGORIES.includes(values.category as never)"
      class="space-y-24"
    >
      <RevInputSelect
        id="report-illicit-content-risk-nature"
        v-model="values.riskNature"
        :error="errors.riskNature"
        :label="i18n(translations.riskNatureLabel)"
        :options="riskNatureOptions"
      />

      <RevInputSelect
        id="report-illicit-content-has-caused-incident"
        v-model="values.hasCausedIncident"
        :error="errors.hasCausedIncident"
        :label="i18n(translations.hasCausedIncidentLabel)"
        :options="yesNoUnknownOptions"
      />

      <RevInputSelect
        id="report-illicit-content-is-subject-to-recall"
        v-model="values.isSubjectToRecall"
        :error="errors.isSubjectToRecall"
        :label="i18n(translations.isSubjectToRecallLabel)"
        :options="yesNoUnknownOptions"
      />
    </div>

    <RevInputTextarea
      id="report-illicit-content-description"
      v-model="values.description"
      :description="i18n(translations.descriptionHelper)"
      :error="errors.description"
      :label="i18n(translations.descriptionLabel)"
    />

    <RevInputText
      id="report-illicit-content-content-url"
      v-model="values.contentUrl"
      :description="i18n(translations.contentUrlHelper)"
      :error="errors.contentUrl"
      :label="i18n(translations.contentUrlLabel)"
      type="url"
    />

    <RevInputFileUpload
      id="report-illicit-content-evidence"
      :accept="EVIDENCE_FILE_ACCEPT"
      :description="evidenceMessage"
      :disabled
      :errors="fileErrors"
      :label-empty="i18n(translations.evidenceLabel)"
      :label-not-empty="i18n(translations.evidenceLabel)"
      :max-file-size="MAX_EVIDENCE_FILE_SIZE"
      :max-files="MAX_EVIDENCE_FILES"
      :model-value="files"
      @errors="onFileErrors"
      @update:model-value="onFilesUpdate"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import {
  type UploadErrorMessageOverrides,
  getUploadErrorMessage,
} from '@backmarket/nuxt-module-upload/getUploadErrorMessage'
import { RevInputFileUpload } from '@ds/components/InputFileUpload'
import type { FileError } from '@ds/components/InputFileUpload/InputFileUpload.type'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevInputTextarea } from '@ds/components/InputTextarea'
import { RevRadio } from '@ds/components/Radio'

import {
  CATEGORY_OPTION_VALUES,
  CONDITIONAL_CATEGORIES,
  EVIDENCE_FILE_ACCEPT,
  MAX_EVIDENCE_FILES,
  MAX_EVIDENCE_FILE_SIZE,
} from '../../../ReportIllicitContent.constants'
import type { ReportIllicitContentFormValues } from '../../../ReportIllicitContent.types'

import translations from './ReportIllicitContentStepContent.translations'

type CategoryOptionValue = (typeof CATEGORY_OPTION_VALUES)[number]

const CATEGORY_OPTION_SHORT_LABELS: Record<
  CategoryOptionValue,
  keyof typeof translations
> = {
  illegal_content: 'categoryOptionIllegalContentShort',
  counterfeit_or_ip_infringement: 'categoryOptionCounterfeitShort',
  misleading_advertising: 'categoryOptionMisleadingAdvertisingShort',
  dangerous_product: 'categoryOptionDangerousProductShort',
  recalled_or_prohibited: 'categoryOptionRecalledOrProhibitedShort',
}

const CATEGORY_OPTION_FULL_LABELS: Record<
  CategoryOptionValue,
  keyof typeof translations
> = {
  illegal_content: 'categoryOptionIllegalContentFull',
  counterfeit_or_ip_infringement: 'categoryOptionCounterfeitFull',
  misleading_advertising: 'categoryOptionMisleadingAdvertisingFull',
  dangerous_product: 'categoryOptionDangerousProductFull',
  recalled_or_prohibited: 'categoryOptionRecalledOrProhibitedFull',
}

type StepValues = Pick<
  ReportIllicitContentFormValues,
  | 'category'
  | 'riskNature'
  | 'hasCausedIncident'
  | 'isSubjectToRecall'
  | 'description'
  | 'contentUrl'
>

type StepErrors = {
  [K in keyof StepValues]?: string
}

defineProps<{
  disabled: boolean
  errors: StepErrors
  riskNatureOptions: Array<{ value: string; label: string }>
  yesNoUnknownOptions: Array<{ value: string; label: string }>
}>()

const values = defineModel<StepValues>({ required: true })
const files = defineModel<File[]>('files', { required: true })

const i18n = useI18n()

const fileErrorMessages: UploadErrorMessageOverrides = {
  MAX_FILES_EXCEEDED: translations.evidenceTooMany,
  MAX_FILE_SIZE_EXCEEDED: translations.evidenceTooLarge,
  WRONG_FILE_EXTENSION: translations.evidenceWrongFormat,
}

const fileErrors = ref<FileError[]>([])

// The design-system input emits `errors` then `update:modelValue` for the same
// selection: keep the errors of the current selection, clear older ones.
let hasNewFileErrors = false

const evidenceMessage = computed(() => {
  const [error] = fileErrors.value

  return error
    ? i18n(getUploadErrorMessage(error, fileErrorMessages))
    : i18n(translations.evidenceDescription)
})

function onFileErrors(errors: FileError[]) {
  fileErrors.value = errors
  hasNewFileErrors = true
}

function onFilesUpdate(nextFiles: File[]) {
  if (!hasNewFileErrors) fileErrors.value = []
  hasNewFileErrors = false
  files.value = nextFiles
}

const categoryOptions = computed(() =>
  CATEGORY_OPTION_VALUES.map((value) => ({
    value,
    short: i18n(translations[CATEGORY_OPTION_SHORT_LABELS[value]]),
    full: i18n(translations[CATEGORY_OPTION_FULL_LABELS[value]]),
  })),
)
</script>
